import Session from 'supertokens-web-js/recipe/session';

/**
 * OAuth endpoints live at `/apps/*` on the API origin — outside the
 * `/api` prefix used by the rest of the admin code. This is per the
 * standard OAuth contract path (see docs/apps-platform/contract-v1.md).
 * Use this helper instead of the regular api client when calling them.
 */
const OAUTH_BASE = import.meta.env.VITE_API_URL || '';

/**
 * Carries the structured OAuth error response so callers (notably the
 * consent page) can branch on the standard `error` code instead of
 * regex-matching the description string.
 */
export class OAuthError extends Error {
  code: string;
  description: string;
  status: number;
  constructor(code: string, description: string, status: number) {
    super(description || code);
    this.name = 'OAuthError';
    this.code = code;
    this.description = description;
    this.status = status;
  }
}

export async function fetchOAuth<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  };
  try {
    if (await Session.doesSessionExist()) {
      const token = await Session.getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    /* no session */
  }
  const res = await fetch(`${OAUTH_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = (body as any)?.error || 'request_failed';
    const description =
      (body as any)?.error_description ||
      (body as any)?.message ||
      `HTTP ${res.status}`;
    throw new OAuthError(code, description, res.status);
  }
  return body as T;
}
